import { Edit05, ShoppingBag01 } from "@untitledui/icons";
import { Focusable } from "react-aria-components";
import type { OpenCollaborationDetail } from "@/api/collaborations";
import { useMerchantDetails } from "@/api/merchant-details";
import { Button } from "@/components/base/buttons/button";
import { Tooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";
import { useMerchant } from "@/providers/MerchantProvider";
import { buildTikTokCollaborationUrl } from "@/utils/tiktok-urls";
import { CollaborationSequenceOverrides } from "./collaboration-sequence-overrides";
import { ReviewSampleRequestsButton } from "./review-sample-requests-button";

type Props = {
  collaboration: OpenCollaborationDetail;
  sampleRequestedCount?: number;
};

/**
 * Formats a commission rate from database format (decimal) to display format.
 * The rate is stored as a decimal (e.g., 0.10 for 10%)
 */
const formatCommissionRate = (rate: number | null | undefined): string => {
  if (rate === undefined || rate === null) return "N/A";
  return `${(rate * 100).toFixed(2)}%`;
};

/**
 * Formats a currency value to display with dynamic currency.
 */
const formatCurrency = (value?: number, currency?: string) => {
  if (value === undefined) return "N/A";
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: currency || "GBP",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

const numberFormatter = new Intl.NumberFormat("en-US");

const MetadataItem = ({ label, value }: { label: string; value: string }) => (
  <div className="flex flex-col">
    <span className="text-sm text-tertiary">{value}</span>
    <span className="text-sm text-quaternary">{label}</span>
  </div>
);

const Divider = () => <div className="h-8 w-px bg-border-secondary" />;

export const OpenCollaborationDetailHeader = ({ collaboration, sampleRequestedCount = 0 }: Props) => {
  const merchant = useMerchant();
  const { data: merchantDetails } = useMerchantDetails(merchant);

  const tiktokUrl = buildTikTokCollaborationUrl(collaboration.collaborationId, merchantDetails?.ttsRegion);

  return (
    <div className="flex flex-row justify-between items-start mb-12">
      {/* Left Side - Product image, title, and metadata */}
      <div className="flex flex-row gap-4 items-center min-w-0">
        {/* Product Image */}
        {collaboration.productImageUrl ? (
          <img
            src={collaboration.productImageUrl}
            alt={collaboration.productTitle}
            className="basis-20.5 h-20.5 rounded-lg object-cover flex-shrink-0"
          />
        ) : (
          <div className="flex basis-20.5 h-20.5 flex-1 items-center justify-center rounded-lg bg-gray-100 flex-shrink-0">
            <ShoppingBag01 className="h-8 w-8 text-gray-400" />
          </div>
        )}

        <div className="flex flex-col gap-4 min-w-0">
          {/* Title and price */}
          <div className="flex flex-col min-w-0">
            <Tooltip title={collaboration.productTitle}>
              <Focusable>
                <h1 className="inline-block min-w-0 max-w-[80ch] truncate text-left text-xl text-primary outline-hidden">
                  {collaboration.productTitle}
                </h1>
              </Focusable>
            </Tooltip>
            {collaboration.productPrice !== undefined && (
              <span className="text-sm text-tertiary">
                {formatCurrency(collaboration.productPrice, collaboration.productPriceCurrency)}
              </span>
            )}
          </div>

          {/* Metadata row */}
          <div className="flex gap-10 items-center">
            <MetadataItem label="Type" value="Open" />
            <Divider />
            <MetadataItem
              label="Stock"
              value={collaboration.stock !== undefined ? numberFormatter.format(collaboration.stock) : "N/A"}
            />
            <Divider />
            <MetadataItem label="Standard rate" value={formatCommissionRate(collaboration.commissionRate)} />
            <Divider />
            {/* Shop Ads - placeholder for future implementation */}
            <MetadataItem label="Shop Ads" value="N/A" />
          </div>
        </div>
      </div>

      {/* Right Side - Action buttons */}
      <div className="flex items-center gap-1.5">
        <ReviewSampleRequestsButton
          collaborationId={collaboration.collaborationId}
          sampleRequestedCount={sampleRequestedCount}
          collaborationType="open"
        />
        <CollaborationSequenceOverrides
          collaborationId={collaboration.collaborationId}
          collaborationName={collaboration.productTitle}
        />
        <Button color="secondary" size="sm" href={tiktokUrl} target="_blank" rel="noopener noreferrer">
          Collaboration details
        </Button>
        <Tooltip title="Open Collaborations cannot be currently created or edited in Unbox.">
          <TooltipTrigger>
            <span>
              <Button color="secondary" size="sm" iconLeading={<Edit05 className="w-4 h-4" />} isDisabled>
                Edit
              </Button>
            </span>
          </TooltipTrigger>
        </Tooltip>
      </div>
    </div>
  );
};
